import React from 'react';
import { Shield, UserRound, Cpu, Cookie, Mail } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { SUPPORT_EMAIL, SUPPORT_EMAIL_MAILTO } from '@/config/links';

const Policy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Privacy Policy"
        description="Privacy policy for Boxd Logistics outlining how we collect, use, and protect user information."
        canonical="https://boxdlogistics.in/policy"
        robots="noindex, follow"
      />
      <Navbar />

      <section className="relative pt-32 pb-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        ></div>
        <div className="absolute -top-12 right-0 w-72 h-72 bg-gradient-to-br from-blue-300/20 to-blue-700/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-900 px-4 py-2 text-sm font-semibold mb-6">
            <Shield className="w-4 h-4" />
            Privacy Policy
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl">
            Your privacy is important to us. This policy outlines how Boxd Logistics collects, uses, maintains, and discloses information from website users.
          </p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <UserRound className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">Personal Identification Information</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              We may collect personal identification information from users in various ways, including when users visit our site, register, place an order, fill out forms, or interact with our services.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              Users may be asked for details such as name, email address, mailing address, phone number, and other relevant information. Users may also visit our site anonymously. We only collect personal information if it is voluntarily submitted.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Cpu className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">Non-personal Identification Information</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              We may collect non-personal identification information whenever users interact with our site. This may include browser name, device type, operating system, internet service provider details, and other technical connection information.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Cookie className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">Web Browser Cookies</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Our site may use cookies to enhance user experience and improve functionality. Users can choose to set their browser to refuse cookies or alert when cookies are being sent. Some parts of the site may not function properly if cookies are disabled.
            </p>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Mail className="w-5 h-5 text-blue-900 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at{' '}
              <a href={SUPPORT_EMAIL_MAILTO} className="text-blue-900 hover:text-blue-950 font-semibold">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Policy;
